import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingContext, BufferService, M_Header, M_Button } from 'src/components/M_Components'
import { Description, Print, Download } from '@mui/icons-material'
import { Chip, Paper, Divider, Box, Typography } from '@mui/material'

export default function ViewCmrDocument() {
    const ctx = useContext(LoadingContext)
    const { t } = useTranslation()
    const [cmrDocument, setCmrDocument] = useState(null)
    const [serviceOrder, setServiceOrder] = useState(null)
    const bufferServiceCmr = new BufferService('CmrDocument')
    const bufferServiceOrder = new BufferService('ServiceOrder')

    useEffect(() => {
        const cmrDoc = ctx.passValue?.get('cmrDocument')
        if (cmrDoc) {
            setCmrDocument(cmrDoc)
            loadCmrDocumentDetails(cmrDoc.CmrDocumentId)
            loadServiceOrderDetails(cmrDoc.ServiceOrderId)
        }
    }, [])

    const loadCmrDocumentDetails = async (cmrDocumentId) => {
        try {
            ctx.setIsLoad(true)
            const response = await bufferServiceCmr.getId(cmrDocumentId)
            if (response && !response.response && response.name !== 'AxiosError') {
                setCmrDocument(response)
            }
        } catch (error) {
            console.error('Error loading CMR document:', error)
            ctx.toastMessages('error', 'Error loading CMR document', '')
        } finally {
            ctx.setIsLoad(false)
        }
    }

    const loadServiceOrderDetails = async (serviceOrderId) => {
        try {
            const response = await bufferServiceOrder.getId(serviceOrderId)
            if (response && !response.response && response.name !== 'AxiosError') {
                setServiceOrder(response)
            }
        } catch (error) {
            console.error('Error loading ServiceOrder:', error)
        }
    }

    const formatDate = (date) => {
        if (!date) return '-'
        return new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    }

    const formatDateTime = (date) => {
        if (!date) return '-'
        return new Date(date).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getStateColor = (state) => {
        switch (state) {
            case 0: return 'default'
            case 1: return 'info'
            case 2: return 'success'
            case 3: return 'warning'
            case 4: return 'secondary'
            default: return 'default'
        }
    }

    const getStateLabel = (state) => {
        switch (state) {
            case 0: return 'Draft'
            case 1: return 'Generated'
            case 2: return 'Approved'
            case 3: return 'Updated'
            case 4: return 'Closed'
            default: return 'Unknown'
        }
    }

    const handlePrint = () => {
        window.print()
    }

    const handleDownload = () => {
        // TODO: Implement PDF download
        ctx.toastMessages('info', 'PDF download feature coming soon', '')
    }

    if (!cmrDocument) {
        return (
            <div style={{ padding: '20px' }}>
                <M_Header title={t('CMR Document')} icon={<Description />} />
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <p>No CMR document selected</p>
                </div>
            </div>
        )
    }

    return (
        <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <M_Header
                title={`CMR ${cmrDocument.DocumentNumber || ''}`}
                icon={<Description />}
                element={
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Chip
                            label={getStateLabel(cmrDocument.State)}
                            color={getStateColor(cmrDocument.State)}
                            size="medium"
                        />
                        <M_Button
                            label={t('Print') || 'Print'}
                            icon={<Print />}
                            onClick={handlePrint}
                            size="small"
                        />
                        <M_Button
                            label={t('Download') || 'Download'}
                            icon={<Download />}
                            onClick={handleDownload}
                            size="small"
                        />
                    </div>
                }
            />

            <Paper
                elevation={3}
                style={{
                    marginTop: '20px',
                    padding: '40px',
                    maxWidth: '210mm',
                    margin: '20px auto',
                    backgroundColor: 'white',
                    minHeight: '297mm'
                }}
            >
                {/* CMR Document Header */}
                <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '3px solid #000', paddingBottom: '20px' }}>
                    <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                        CONSIGNMENT NOTE (CMR)
                    </Typography>
                    <Typography variant="body1" style={{ fontSize: '12px' }}>
                        Convention relative au contrat de transport international de marchandises par route (CMR)
                    </Typography>
                </div>

                {/* Document Information */}
                <Box style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                            Document Number:
                        </Typography>
                        <Typography variant="body1">{cmrDocument.DocumentNumber || '-'}</Typography>
                    </Box>
                    <Box style={{ textAlign: 'right' }}>
                        <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                            Issue Date:
                        </Typography>
                        <Typography variant="body1">{formatDate(cmrDocument.IssueDateTime)}</Typography>
                    </Box>
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Consignor (Shipper) Section */}
                <Box style={{ marginBottom: '25px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                        1. CONSIGNOR (SENDER)
                    </Typography>
                    {serviceOrder?.Customer ? (
                        <Box style={{ paddingLeft: '15px' }}>
                            <Typography variant="body1" style={{ marginBottom: '5px' }}>
                                <strong>Name:</strong> {serviceOrder.Customer}
                            </Typography>
                            {serviceOrder.CustomerAddress && serviceOrder.CustomerAddress.length > 0 && (
                                <Box style={{ marginTop: '10px' }}>
                                    {serviceOrder.CustomerAddress.map((addr, idx) => (
                                        <Typography key={idx} variant="body2" style={{ marginBottom: '3px' }}>
                                            {addr.AddressName}, {addr.AddressStreet}, {addr.AddressPlace}, {addr.AddressZipCode}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2" style={{ fontStyle: 'italic', color: '#666' }}>
                            No consignor information available
                        </Typography>
                    )}
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Consignee (Receiver) Section */}
                <Box style={{ marginBottom: '25px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                        2. CONSIGNEE (RECEIVER)
                    </Typography>
                    {serviceOrder?.Receiver ? (
                        <Box style={{ paddingLeft: '15px' }}>
                            <Typography variant="body1" style={{ marginBottom: '5px' }}>
                                <strong>Name:</strong> {serviceOrder.Receiver}
                            </Typography>
                        </Box>
                    ) : serviceOrder?.Address && serviceOrder.Address.length > 0 ? (
                        <Box style={{ paddingLeft: '15px' }}>
                            {serviceOrder.Address.filter(addr => addr.AddressType === 'Delivery' || addr.Sequence > 0).map((addr, idx) => (
                                <Box key={idx} style={{ marginBottom: '10px' }}>
                                    <Typography variant="body1" style={{ marginBottom: '5px' }}>
                                        <strong>Address {idx + 1}:</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                        {addr.AddressName || addr.AddressStreet}, {addr.AddressPlace}, {addr.AddressZipCode}, {addr.AddressCountryCode}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" style={{ fontStyle: 'italic', color: '#666' }}>
                            No consignee information available
                        </Typography>
                    )}
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Carrier Section */}
                <Box style={{ marginBottom: '25px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                        3. CARRIER
                    </Typography>
                    {serviceOrder?.TransportResourceResponse ? (
                        <Box style={{ paddingLeft: '15px' }}>
                            <Typography variant="body2" style={{ marginBottom: '5px' }}>
                                Transport Resource ID: {serviceOrder.TransportResourceId}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body2" style={{ fontStyle: 'italic', color: '#666' }}>
                            No carrier information available
                        </Typography>
                    )}
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Place and Date of Taking Over Goods */}
                <Box style={{ marginBottom: '25px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                        4. PLACE AND DATE OF TAKING OVER THE GOODS
                    </Typography>
                    <Box style={{ paddingLeft: '15px', display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                Place:
                            </Typography>
                            {serviceOrder?.Address && serviceOrder.Address.length > 0 ? (
                                <Typography variant="body1">
                                    {serviceOrder.Address[0]?.AddressName || serviceOrder.Address[0]?.AddressPlace || '-'}
                                </Typography>
                            ) : (
                                <Typography variant="body1">-</Typography>
                            )}
                        </Box>
                        <Box>
                            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                Date:
                            </Typography>
                            <Typography variant="body1">
                                {formatDate(serviceOrder?.TransportDate) || formatDate(cmrDocument.IssueDateTime) || '-'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Place Designated for Delivery */}
                <Box style={{ marginBottom: '25px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                        5. PLACE DESIGNATED FOR DELIVERY
                    </Typography>
                    <Box style={{ paddingLeft: '15px' }}>
                        {serviceOrder?.Address && serviceOrder.Address.length > 1 ? (
                            <Typography variant="body1">
                                {serviceOrder.Address[serviceOrder.Address.length - 1]?.AddressName || 
                                 serviceOrder.Address[serviceOrder.Address.length - 1]?.AddressPlace || '-'}
                            </Typography>
                        ) : serviceOrder?.Address && serviceOrder.Address.length === 1 ? (
                            <Typography variant="body1">
                                {serviceOrder.Address[0]?.AddressName || serviceOrder.Address[0]?.AddressPlace || '-'}
                            </Typography>
                        ) : (
                            <Typography variant="body1">-</Typography>
                        )}
                    </Box>
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Goods Description */}
                <Box style={{ marginBottom: '25px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                        6. GOODS
                    </Typography>
                    <Box style={{ paddingLeft: '15px' }}>
                        <Typography variant="body2" style={{ marginBottom: '10px' }}>
                            <strong>Description:</strong> {serviceOrder?.ReferenceInvoiceClient || 'General cargo'}
                        </Typography>
                        {serviceOrder?.GrossWeight && (
                            <Typography variant="body2" style={{ marginBottom: '5px' }}>
                                <strong>Gross Weight:</strong> {serviceOrder.GrossWeight} {serviceOrder.WeightUnit || 'kg'}
                            </Typography>
                        )}
                        {serviceOrder?.GrossVolume && (
                            <Typography variant="body2" style={{ marginBottom: '5px' }}>
                                <strong>Gross Volume:</strong> {serviceOrder.GrossVolume} {serviceOrder.VolumeUnit || 'm³'}
                            </Typography>
                        )}
                        {serviceOrder?.Distance && (
                            <Typography variant="body2" style={{ marginBottom: '5px' }}>
                                <strong>Distance:</strong> {serviceOrder.Distance} km
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Special Instructions */}
                {cmrDocument.Notes && (
                    <>
                        <Box style={{ marginBottom: '25px' }}>
                            <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                                7. SPECIAL INSTRUCTIONS
                            </Typography>
                            <Box style={{ paddingLeft: '15px' }}>
                                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                                    {cmrDocument.Notes}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider style={{ margin: '20px 0' }} />
                    </>
                )}

                {/* Driver Notes */}
                {cmrDocument.DriverNotes && (
                    <>
                        <Box style={{ marginBottom: '25px' }}>
                            <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                                8. DRIVER NOTES
                            </Typography>
                            <Box style={{ paddingLeft: '15px' }}>
                                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                                    {cmrDocument.DriverNotes}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider style={{ margin: '20px 0' }} />
                    </>
                )}

                {/* Reservations and Observations */}
                <Box style={{ marginBottom: '25px', minHeight: '100px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                        9. RESERVATIONS AND OBSERVATIONS
                    </Typography>
                    <Box style={{ paddingLeft: '15px', minHeight: '80px', border: '1px solid #ddd', padding: '10px' }}>
                        <Typography variant="body2" style={{ fontStyle: 'italic', color: '#666' }}>
                            {cmrDocument.State === 4 ? 'Document closed' : 'No reservations'}
                        </Typography>
                    </Box>
                </Box>

                <Divider style={{ margin: '20px 0' }} />

                {/* Signatures Section */}
                <Box style={{ marginTop: '40px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '20px', borderBottom: '2px solid #000', paddingBottom: '5px' }}>
                        10. SIGNATURES
                    </Typography>
                    <Box style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
                        <Box style={{ width: '45%', textAlign: 'center' }}>
                            <Box style={{ borderTop: '1px solid #000', paddingTop: '10px', marginTop: '60px' }}>
                                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                    CONSIGNOR
                                </Typography>
                                <Typography variant="body2" style={{ fontSize: '11px', marginTop: '5px' }}>
                                    Date: {formatDate(cmrDocument.IssueDateTime)}
                                </Typography>
                            </Box>
                        </Box>
                        <Box style={{ width: '45%', textAlign: 'center' }}>
                            <Box style={{ borderTop: '1px solid #000', paddingTop: '10px', marginTop: '60px' }}>
                                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                    CARRIER
                                </Typography>
                                <Typography variant="body2" style={{ fontSize: '11px', marginTop: '5px' }}>
                                    Date: {formatDate(cmrDocument.StateChangedAt)}
                                </Typography>
                                {cmrDocument.StateChangedBy && (
                                    <Typography variant="body2" style={{ fontSize: '11px', marginTop: '5px' }}>
                                        By: {cmrDocument.StateChangedBy}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Footer Information */}
                <Box style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #ddd', fontSize: '11px', color: '#666' }}>
                    <Typography variant="body2" style={{ marginBottom: '5px' }}>
                        <strong>Service Order:</strong> #{serviceOrder?.DocNumber || cmrDocument.ServiceOrderDocNumber || '-'}
                    </Typography>
                    {cmrDocument.EcmrUuid && (
                        <Typography variant="body2" style={{ marginBottom: '5px' }}>
                            <strong>eCMR UUID:</strong> {cmrDocument.EcmrUuid}
                        </Typography>
                    )}
                    {cmrDocument.IsEcmrIntegrated && (
                        <Typography variant="body2" style={{ marginBottom: '5px' }}>
                            <strong>eCMR Status:</strong> Integrated {cmrDocument.EcmrUploadedAt && `(Uploaded: ${formatDateTime(cmrDocument.EcmrUploadedAt)})`}
                        </Typography>
                    )}
                    <Typography variant="body2">
                        <strong>Document State:</strong> {getStateLabel(cmrDocument.State)}
                    </Typography>
                </Box>
            </Paper>
        </div>
    )
}

